import { useMutation, useQuery } from "@apollo/client";
import { UserGroupIcon } from "@heroicons/react/24/outline";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { Button } from "@mui/material";
import { Fragment } from "react";
import styled from "styled-components";
import EmptyState from "../../components/EmptyState/EmptyState";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Loader from "../../components/Loader/Loader";
import Page from "../../components/Page/Page";
import Panel from "../../components/Panel/Panel";
import Badge from "../../components/Styled/Badge";
import { Row } from "../../components/Styled/Container";
import { MediaFrame, MediaImage } from "../../components/Styled/Media";
import { Group } from "../../graphql/graphql";
import { APPROVE_GROUP } from "../../graphql/mutations-gql";
import { GROUPS } from "../../graphql/queries-gql";

const GroupLayout = styled.div`
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  gap: 20px;
  align-items: start;

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Thumb = styled(MediaFrame)`
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.border};
`;

const Fields = styled.dl`
  display: grid;
  grid-template-columns: minmax(140px, 200px) minmax(0, 1fr);
  margin: 0;
  font-size: 13px;
`;

const FieldKey = styled.dt`
  padding: 6px 12px 6px 0;
  border-bottom: 1px solid ${(props) => props.theme.border};
  color: ${(props) => props.theme.secondaryTextColor};
`;

const FieldValue = styled.dd`
  margin: 0;
  padding: 6px 0;
  border-bottom: 1px solid ${(props) => props.theme.border};
  color: ${(props) => props.theme.textColor};
  font-family: ${(props) => props.theme.monoFontFamily};
  font-size: 12px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const Groups = () => {
  const { data, loading, error } = useQuery(GROUPS);
  const [approveGroup] = useMutation(APPROVE_GROUP);

  const groups: Group[] = data?.groups ?? [];

  const showImage = (src: string) => {
    //window.location.href = src;
    window.open(src, "_blank");
  };

  const handleApprove = (value: boolean, groupId: string) => {
    approveGroup({
      variables: {
        groupId,
        value,
      },
      onCompleted: (data) => {
        console.log("approveGroup onCompleted", data);
      },
      onError: (error) => {
        console.log("approveGroup onError", error);
      },
      refetchQueries: [GROUPS],
    });
  };

  return (
    <Page
      title="Groups"
      description="Public groups waiting for review."
      badge={
        groups.length > 0 && (
          <Badge $tone="primary">{groups.length} waiting</Badge>
        )
      }
    >
      {loading && (
        <Row justifyContent="center">
          <Loader />
        </Row>
      )}
      {error && <ErrorAlert error={error} />}

      {!loading && !error && groups.length === 0 && (
        <EmptyState
          Icon={UserGroupIcon}
          title="No groups to review"
          description="New public groups show up here."
        />
      )}

      {groups.map((group) => (
        <Panel
          key={group._id}
          title={group.name || "Untitled group"}
          actions={
            <Row colGap={8}>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<XMarkIcon width={15} />}
                onClick={() => handleApprove(false, group._id)}
              >
                Reject
              </Button>
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckIcon width={15} />}
                onClick={() => handleApprove(true, group._id)}
              >
                Approve
              </Button>
            </Row>
          }
        >
          <GroupLayout>
            <Thumb
              $ratio="1 / 1"
              title="Open photo"
              onClick={() => showImage(group.photo?.url ?? "")}
            >
              <MediaImage
                src={group.photo?.thumbnailUrl ?? ""}
                alt=""
                loading="lazy"
              />
            </Thumb>
            <Fields>
              {(Object.keys(group) as (keyof Group)[]).map((key) => (
                <Fragment key={key}>
                  <FieldKey>{key}</FieldKey>
                  <FieldValue>{JSON.stringify(group[key], null, 2)}</FieldValue>
                </Fragment>
              ))}
            </Fields>
          </GroupLayout>
        </Panel>
      ))}
    </Page>
  );
};
export default Groups;
